import {
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCardTitle,
} from "mdb-react-ui-kit";
import {
  renderListItems,
  renderDoiItem,
  renderRetractionWatchItem,
  renderDoiReferenceList,
} from "./ReferenceAnalysisReviewUtils";
import { InfoTooltip } from "../../../components";
import { ReportActionButtons } from "../../screened-manuscripts/components/ReportActionButtons";
import { ApplyAction } from "../../../components/ApplyAction";
import { dropdownOptions } from "../../screened-manuscripts/dropdownOptions";
import { renderCrossrefChecksItem } from "./ReferenceAnalysisCrossrefChecks";
import { renderContextChecksItem } from "./ReferenceAnalysisContextChecks";

const ReferenceAnalysisDataReview = ({
  data,
  onActionSelect,
  onActionUndo,
}) => {
  const retractionWatchData =
    data?.screening_outcomes?.find((x) => x.type === "retractionWatch") || {};
  const doiAnalysisData =
    data?.screening_outcomes?.find((x) => x.type === "doiAnalysis") || {};
  const crossrefChecksData =
    data?.screening_outcomes?.find((x) => x.type === "crossrefChecks") || {};
  const contextChecksData =
    data?.screening_outcomes?.find((x) => x.type === "contextChecks") || {};

  const retractionWatchItems = retractionWatchData.items ?? [];
  const retractionWatchCount = retractionWatchItems.length;

  // Split DOI analysis: all hits are in one list; distinguish by doi value.
  // Invalid DOI = doi has a value; Unidentified DOI = doi empty or null.
  const rawDoiItems = doiAnalysisData.items ?? [];
  const hasLegacyDoiStructure =
    rawDoiItems[0]?.invalid != null || rawDoiItems[0]?.unidentified != null;
  const doiAnalysisItems = hasLegacyDoiStructure
    ? (doiAnalysisData.items?.[0]?.invalid ?? rawDoiItems.filter((item) => item?.DOI) ?? [])
    : rawDoiItems.filter(
        (item) => (item?.doi ?? item?.DOI) != null && String(item?.doi ?? item?.DOI).trim() !== ""
      );
  const unidentifiedItems = hasLegacyDoiStructure
    ? (doiAnalysisData.items?.[0]?.unidentified ?? [])
    : rawDoiItems.filter(
        (item) => (item?.doi ?? item?.DOI) == null || String(item?.doi ?? item?.DOI).trim() === ""
      );
  const doiAnalysisCount = doiAnalysisItems.length;

  const crossrefChecksItems = crossrefChecksData.items ?? [];
  const crossrefChecksCount =
    (crossrefChecksItems[0]?.ageCheck?.length ?? 0) +
    (crossrefChecksItems[0]?.citationCartel?.length ?? 0) +
    (crossrefChecksItems[0]?.selfCitation?.length ?? 0) +
    (crossrefChecksItems[0]?.titleMatch?.length ?? 0) +
    (crossrefChecksItems[0]?.journalMatch?.length ?? 0);

  const contextChecksItems = contextChecksData.items ?? [];
  const contextChecksCount =
    (contextChecksItems[0]?.uncitedReferences?.length ?? 0) +
    (contextChecksItems[0]?.outOfContextReferences?.length ?? 0);

  const renderHeader = (
    title,
    itemCount,
    supplementaryData,
    showUnidentified = false
  ) => (
    <>
      <span>{title}</span>{' '}
      <span className="text-nowrap">
        {itemCount}
        {supplementaryData?.retrievedReferences !== undefined && (
          <>/{supplementaryData.retrievedReferences}</>
        )}
        {supplementaryData?.totalReferences !== undefined && (
          <> ({supplementaryData.totalReferences})</>
        )}
        {showUnidentified &&
          supplementaryData?.unidentifiedPercent !== undefined &&
          supplementaryData.unidentifiedPercent > 0 && (
            <> – {Math.round(supplementaryData.unidentifiedPercent)}%</>
          )}
      </span>
    </>
  );

  return (
    <>
      {retractionWatchItems.length > 0 && (
        <MDBCard className="my-2">
          <MDBCardHeader className="d-flex align-items-center">
            <ReportActionButtons
              signals={["retractionWatch", "doiAnalysis"]}
              iconOnly
            />
            <MDBCardTitle className="pt-2 ps-2">
              {renderHeader(
                "Retraction Watch",
                retractionWatchCount,
                retractionWatchData.supplementary_data
              )}
              <InfoTooltip
                tooltipText={
                  <>
                    This signal checks if references are present in the
                    Retraction Watch database.
                    <br />
                    &#x25cf; The first number displays the number of references
                    found in the Retraction Watch database.
                    <br />
                    &#x25cf; The second number displays the total number of DOIs
                    that were listed in the reference list or could be retrieved
                    using our software and were used to query the Retraction
                    Watch database.
                    <br />
                    &#x25cf; The number in brackets is the total number of
                    references found in the manuscript.
                  </>
                }
              />
            </MDBCardTitle>
            <div className="ms-auto">
              <ApplyAction
                index={data?.manuscript_id}
                status={data?.submission_status}
                selectAction={onActionSelect}
                undoAction={onActionUndo}
                options={dropdownOptions}
              />
            </div>
          </MDBCardHeader>
          <MDBCardBody>
            {renderListItems(retractionWatchItems, renderRetractionWatchItem)}
          </MDBCardBody>
        </MDBCard>
      )}

      {(doiAnalysisItems.length > 0 ||
        unidentifiedItems.length > 0 ||
        doiAnalysisData.items?.length > 0) && (
          <MDBCard className="mt-3">
            <MDBCardHeader className="d-flex align-items-center">
              <ReportActionButtons
                signals={["doiAnalysis"]}
                iconOnly
              />
              <MDBCardTitle className="pt-2 ps-2">
                {renderHeader(
                  "DOI Analysis",
                  doiAnalysisCount,
                  doiAnalysisData.supplementary_data,
                  unidentifiedItems.length > 0
                )}
                <InfoTooltip
                  tooltipText={
                    <>
                      This signal analyses references in the manuscript.
                      <br />
                      &#x25cf; The first number signifies the number of DOIs
                      listed in the reference list that do not exist (via check
                      with DOI.org).
                      <br />
                      &#x25cf; The second number signifies the total number of
                      DOIs that were listed in the reference list or could be
                      retrieved using our software.
                      <br />
                      &#x25cf; The number in brackets is the total number of
                      references found in the manuscript.
                    </>
                  }
                />
              </MDBCardTitle>
              <div className="ms-auto">
                <ApplyAction
                  index={data?.manuscript_id}
                  status={data?.submission_status}
                  selectAction={onActionSelect}
                  undoAction={onActionUndo}
                  options={dropdownOptions}
                />
              </div>
            </MDBCardHeader>
            <MDBCardBody>
              {doiAnalysisItems.length > 0 && (
                <div className="border rounded p-3">
                  <div className="mb-2">
                    <span className="fw-bold text-dark">Invalid DOI:</span> {doiAnalysisItems.length}
                  </div>
                  {renderDoiReferenceList(doiAnalysisItems)}
                </div>
              )}

              {doiAnalysisItems.length > 0 && unidentifiedItems.length > 0 && <div className="mt-3" />}

              {unidentifiedItems.length > 0 && (
                <div className="border rounded p-3">
                  <div className="mb-2">
                    <span className="fw-bold text-dark">Unidentified DOI:</span> {unidentifiedItems.length}
                  </div>
                  {renderDoiReferenceList(unidentifiedItems, false)}
                </div>
              )}
            </MDBCardBody>
          </MDBCard>
        )}

      {crossrefChecksItems.length > 0 && (
        <MDBCard className="mt-3">
          <MDBCardHeader className="d-flex align-items-center">
            <ReportActionButtons
              signals={["crossrefChecks"]}
              iconOnly
            />
            <MDBCardTitle className="pt-2 ps-2">
              {renderHeader("Crossref checks:", crossrefChecksCount)}
              <InfoTooltip
                tooltipText={
                  <>
                    Checks performed on Crossref dataset, for references with
                    valid DOI registered by Crossref.
                    <br />
                    &#x25cf; <b>Age check</b>: References observed to be older
                    than 10 years
                    <br />
                    &#x25cf; <b>Citation cartel</b>: Excessive citations to the
                    same article
                    <br />
                    &#x25cf; <b>Self-citation</b>: Excessive citations to
                    articles from the author(s) of the manuscript
                    <br />
                    &#x25cf; <b>Title comparison</b>: Title and journal from
                    reference list compared to Crossref data
                  </>
                }
              />
            </MDBCardTitle>
            <div className="ms-auto">
              <ApplyAction
                index={data?.manuscript_id}
                status={data?.submission_status}
                selectAction={onActionSelect}
                undoAction={onActionUndo}
                options={dropdownOptions}
              />
            </div>
          </MDBCardHeader>
          <MDBCardBody>
            {renderListItems(crossrefChecksItems, renderCrossrefChecksItem)}
          </MDBCardBody>
        </MDBCard>
      )}

      {contextChecksItems.length > 0 && (
        <MDBCard className="mt-3">
          <MDBCardHeader className="d-flex align-items-center">
            <ReportActionButtons
              signals={["contextChecks"]}
              iconOnly
            />
            <MDBCardTitle className="pt-2 ps-2">
              {renderHeader(
                "Context checks",
                contextChecksCount,
                contextChecksData.supplementary_data
              )}
              <InfoTooltip
                tooltipText={
                  <>
                    Checks for reference usage and context within the manuscript.
                    <br />
                    &#x25cf; The first number displays the number of issues found (unused or out-of-context references).
                    <br />
                    &#x25cf; The second number displays the total number of references analyzed.
                    <br />
                    &#x25cf; The number in brackets is the total number of references found in the manuscript.
                    <br /><br />
                    <b>Unused references</b>: References that are not cited anywhere in the manuscript
                    <br />
                    <b>Out-of-context references</b>: References that are cited in unrelated context
                  </>
                }
              />
            </MDBCardTitle>
            <div className="ms-auto">
              <ApplyAction
                index={data?.manuscript_id}
                status={data?.submission_status}
                selectAction={onActionSelect}
                undoAction={onActionUndo}
                options={dropdownOptions}
              />
            </div>
          </MDBCardHeader>
          <MDBCardBody>
            {contextChecksItems.length > 0 && renderContextChecksItem(contextChecksItems[0])}
          </MDBCardBody>
        </MDBCard>
      )}
    </>
  );
};

export default ReferenceAnalysisDataReview;